<script lang="ts">
	import { metadata } from '$lib/content/data/metadata';
	import { getCohortCourseBySlug } from '$lib/content/data/cohort-courses';
	import { cohortStats } from '$lib/types/cohort-course';
	import { buildCourseIcs, sessionDayList } from '$lib/utils/calendar';
	import EnrollDialog from '$lib/components/EnrollDialog.svelte';
	import LocalSessionTime from '$lib/components/LocalSessionTime.svelte';

	// Dedicated free-session detail page (restored 2026-10-18 at its own URL —
	// the paid course owns /courses/agentic-ai-masterclass). Static route, so
	// the entry is resolved directly instead of via a [slug] param.
	const course = getCohortCourseBySlug('free-agentic-ai-masterclass')!;
	const stats = cohortStats(course);
	const icsHref = buildCourseIcs(course);

	// Enroll dialog state
	let enrollOpen = $state(false);

	// Accordion state — first agenda block open by default
	let expandedBlock = $state<number | null>(0);

	function toggleBlock(index: number) {
		expandedBlock = expandedBlock === index ? null : index;
	}

	const levelLabel = $derived(course.level.charAt(0).toUpperCase() + course.level.slice(1));

	const pageTitle = $derived(`${course.title} — Agenda & Free Enrollment | Akonia Codex`);

	// Course Schema (JSON-LD)
	const courseSchema = $derived({
		'@context': 'https://schema.org',
		'@type': 'Course',
		name: course.title,
		description: course.description,
		provider: {
			'@type': 'Organization',
			name: 'Akonia Codex',
			url: metadata.company.website
		},
		offers: {
			'@type': 'Offer',
			price: course.price,
			priceCurrency: 'INR',
			category: 'training',
			availability: 'https://schema.org/InStock'
		},
		educationalLevel:
			course.level === 'beginner' ? 'Beginner' : course.level === 'intermediate' ? 'Intermediate' : 'Advanced',
		hasCourseInstance: {
			'@type': 'CourseInstance',
			courseMode: 'online',
			startDate: course.cohort.startDate
		},
		keywords:
			course.slug === 'free-agentic-ai-masterclass'
				? 'agentic ai masterclass, spec-driven development, bmad method, claude code demo, free ai session, one liner to mvp, live coding india'
				: 'free live session, ai development'
	});
</script>

<svelte:head>
	<title>{pageTitle}</title>
	<link rel="canonical" href="{metadata.company.website}/courses/{course.slug}" />
	<meta name="description" content={course.description} />
	<meta property="og:title" content={pageTitle} />
	<meta property="og:description" content={course.description} />
	<meta property="og:type" content="website" />
	<meta property="og:url" content="{metadata.company.website}/courses/{course.slug}" />
	<meta property="og:image" content="{metadata.company.website}/images/og/masterclass.png" />
	<meta property="og:image:width" content="1200" />
	<meta property="og:image:height" content="630" />
	<meta property="og:image:alt" content="{course.title} — free live session by Akonia Codex" />
	<meta name="twitter:card" content="summary_large_image" />
	<meta name="twitter:title" content={pageTitle} />
	<meta name="twitter:description" content={course.description} />
	<meta name="twitter:image" content="{metadata.company.website}/images/og/masterclass.png" />
	<meta name="twitter:image:alt" content="{course.title} — free live session by Akonia Codex" />

	<!-- Structured Data -->
	{@html `<script type="application/ld+json">${JSON.stringify(courseSchema)}</` + `script>`}

	<!-- BreadcrumbList Structured Data -->
	{@html `<script type="application/ld+json">${JSON.stringify({
		'@context': 'https://schema.org',
		'@type': 'BreadcrumbList',
		itemListElement: [
			{
				'@type': 'ListItem',
				position: 1,
				name: 'Home',
				item: metadata.company.website
			},
			{
				'@type': 'ListItem',
				position: 2,
				name: 'Courses',
				item: `${metadata.company.website}/courses`
			},
			{
				'@type': 'ListItem',
				position: 3,
				name: course.title,
				item: `${metadata.company.website}/courses/${course.slug}`
			}
		]
	})}</` + `script>`}
</svelte:head>

<main id="main-content" class="pb-24 lg:pb-0">
	<!-- Hero -->
	<section class="relative py-12 lg:py-20 overflow-hidden border-b border-ak-border-subtle">
		<div class="absolute inset-0 bg-linear-dark"></div>
		<div class="absolute top-0 left-1/2 -translate-x-1/2 w-150 h-75 bg-linear-glow opacity-30 blur-3xl pointer-events-none"></div>

		<div class="relative container mx-auto px-4">
			<nav class="mb-4 text-sm" aria-label="Breadcrumb">
				<ol class="flex flex-wrap items-center gap-2 text-ak-text-muted">
					<li><a href="/" class="hover:text-ak-wintergreen transition-colors">Home</a></li>
					<li><span class="mx-1">/</span></li>
					<li><a href="/courses" class="hover:text-ak-wintergreen transition-colors">Courses</a></li>
					<li><span class="mx-1">/</span></li>
					<li class="text-ak-text-secondary" aria-current="page">{course.title}</li>
				</ol>
			</nav>

			<div class="grid lg:grid-cols-3 gap-8 lg:gap-10">
				<!-- Session info -->
				<div class="lg:col-span-2">
					<div class="flex flex-wrap items-center gap-2 mb-3">
						{#each course.tags as tag (tag)}
							<span class="px-2.5 py-0.5 text-xs font-medium rounded-full bg-ak-wintergreen-dim text-ak-wintergreen">
								{tag}
							</span>
						{/each}
						<span class="px-2.5 py-0.5 text-xs font-medium rounded-full bg-ak-wintergreen/10 text-ak-wintergreen/80">
							{levelLabel}
						</span>
					</div>

					<h1 class="font-display font-bold mb-3">{course.title}</h1>

					<p class="text-base md:text-lg lg:text-xl text-ak-text-secondary leading-relaxed mb-4 max-w-2xl">
						{course.description}
					</p>

					<!-- Inline stats — metadata, not boxes -->
					<div class="flex flex-wrap gap-x-5 gap-y-1 text-sm text-ak-text-muted">
						<span><span class="text-ak-wintergreen font-semibold">{sessionDayList(course.cohort)}</span> weekly</span>
						<span><span class="text-ak-wintergreen font-semibold">{course.cohort.sessionLength}</span> long</span>
						<span><span class="text-ak-wintergreen font-semibold">{stats.agendaItems}</span> agenda items</span>
						{#if stats.labs > 0}
							<span><span class="text-ak-wintergreen font-semibold">{stats.labs}</span> labs</span>
						{/if}
					</div>
				</div>

				<!-- Enroll card (desktop) -->
				<aside class="hidden lg:block lg:col-span-1">
					<div class="sticky top-24 p-5 bg-linear-card rounded-lg border border-ak-border-subtle">
						<div class="text-sm text-ak-text-muted">Admission</div>
						<div class="font-display text-3xl font-bold text-ak-wintergreen leading-tight">Free</div>

						<button
							type="button"
							onclick={() => (enrollOpen = true)}
							class="block w-full py-3 mt-4 bg-ak-wintergreen text-ak-deep font-semibold text-center rounded-md hover:opacity-90 active:opacity-80 transition-opacity touch-manipulation"
						>
							Enroll Now
						</button>

						<a
							href={icsHref}
							download="{course.slug}.ics"
							class="block w-full py-2.5 mt-2 border border-ak-border-subtle text-ak-text-secondary text-center rounded-md hover:border-ak-wintergreen transition-colors text-sm touch-manipulation"
						>
							Add to calendar
						</a>

						<div class="mt-4 pt-4 border-t border-ak-border-subtle text-sm space-y-1">
							<div class="flex items-center gap-2 text-ak-text-secondary">
								<svg class="w-4 h-4 text-ak-wintergreen shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
									<path stroke-linecap="round" stroke-linejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
								</svg>
								<span>Live on <LocalSessionTime cohort={course.cohort} variant="date" /></span>
							</div>
							<div class="text-ak-text-muted pl-6"><LocalSessionTime cohort={course.cohort} variant="time" /> · {course.cohort.cadence}</div>
						</div>
					</div>
				</aside>
			</div>
		</div>
	</section>

	<!-- What you'll learn — plain checklist, not carded -->
	<section class="py-8 lg:py-12">
		<div class="container mx-auto px-4">
			<h2 class="font-display font-bold mb-4">What you'll learn</h2>
			<ul class="grid sm:grid-cols-2 gap-x-8 gap-y-2.5 max-w-3xl">
				{#each course.learn as item (item)}
					<li class="flex items-start gap-2 text-sm text-ak-text-secondary">
						<svg class="w-4 h-4 text-ak-wintergreen mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
							<path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
						</svg>
						<span>{item}</span>
					</li>
				{/each}
			</ul>
		</div>
	</section>

	<!-- Session agenda — accordion cards -->
	<section class="py-8 lg:py-12">
		<div class="container mx-auto px-4">
			<h2 class="font-display font-bold mb-1">Session agenda</h2>
			<p class="text-sm text-ak-text-muted mb-4"><LocalSessionTime cohort={course.cohort} variant="full" /> · {course.cohort.sessionLength}</p>

			<div class="space-y-3">
				{#each course.agenda as block, blockIndex (block.title)}
					<div class="bg-linear-card rounded-lg border border-ak-border-subtle overflow-hidden">
						<button
							onclick={() => toggleBlock(blockIndex)}
							class="w-full px-4 sm:px-5 py-3.5 hover:bg-ak-deep/30 transition-colors flex items-center justify-between gap-3 text-left touch-manipulation"
							aria-expanded={expandedBlock === blockIndex}
							aria-controls="agenda-block-{blockIndex}"
						>
							<span class="flex items-center gap-3 min-w-0">
								<span class="w-7 h-7 rounded-md bg-ak-wintergreen-dim flex items-center justify-center text-ak-wintergreen font-semibold text-xs shrink-0">
									{blockIndex + 1}
								</span>
								<span class="font-display font-semibold leading-snug">{block.title}</span>
							</span>
							<span class="flex items-center gap-2 shrink-0">
								<span class="text-sm text-ak-text-muted whitespace-nowrap hidden sm:inline">{block.items.length} items</span>
								<svg
									class="w-5 h-5 text-ak-text-muted transition-transform duration-200 {expandedBlock === blockIndex ? 'rotate-180' : ''}"
									fill="none"
									viewBox="0 0 24 24"
									stroke="currentColor"
									stroke-width="2"
								>
									<path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" />
								</svg>
							</span>
						</button>

						{#if expandedBlock === blockIndex}
							<ul class="divide-y divide-ak-border-subtle border-t border-ak-border-subtle" id="agenda-block-{blockIndex}">
								{#each block.items as item (item.title)}
									<li class="px-4 sm:px-5 py-2.5 flex items-center justify-between gap-3 text-sm">
										<span class="flex items-center gap-3 min-w-0">
											<svg class="w-4 h-4 text-ak-text-muted shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
												<path stroke-linecap="round" stroke-linejoin="round" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
												<path stroke-linecap="round" stroke-linejoin="round" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
											</svg>
											<span class="text-ak-text-secondary">{item.title}</span>
										</span>
										<span class="text-ak-text-muted whitespace-nowrap">{item.duration}</span>
									</li>
								{/each}
							</ul>
						{/if}
					</div>
				{/each}
			</div>
		</div>
	</section>

	{#if course.labs && course.labs.length > 0}
		<!-- Labs — divided list, not carded (multi-session courses only) -->
		<section class="py-8 lg:py-12">
			<div class="container mx-auto px-4">
				<h2 class="font-display font-bold mb-1">Labs</h2>
				<p class="text-sm text-ak-text-muted mb-4">
					{stats.labs} labs between sessions — every lab feeds your project.
				</p>

				<ul class="divide-y divide-ak-border-subtle max-w-3xl">
					{#each course.labs as lab (lab.title)}
						<li class="py-3 flex items-start justify-between gap-4">
							<div class="min-w-0">
								<div class="font-medium text-sm mb-0.5">{lab.title}</div>
								<p class="text-sm text-ak-text-muted">{lab.description}</p>
							</div>
							<span class="text-sm text-ak-text-muted whitespace-nowrap shrink-0 mt-0.5">{lab.duration}</span>
						</li>
					{/each}
				</ul>
			</div>
		</section>
	{/if}

	{#if course.highlight}
		<!-- The session's single feature moment -->
		<section class="py-8 lg:py-12">
			<div class="container mx-auto px-4">
				<div class="relative p-5 sm:p-6 lg:p-8 rounded-lg bg-linear-to-br from-ak-wintergreen-dim/30 to-ak-surface border border-ak-wintergreen/20 overflow-hidden">
					<div class="absolute top-0 right-0 w-64 h-64 bg-ak-wintergreen/10 rounded-full blur-3xl pointer-events-none"></div>

					<div class="relative">
						<div class="inline-flex items-center gap-2 px-2.5 py-0.5 rounded-full bg-ak-wintergreen/20 text-ak-wintergreen text-xs font-semibold mb-3">
							<svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
								<path stroke-linecap="round" stroke-linejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
							</svg>
							LIVE DEMO
						</div>

						<h2 class="font-display font-bold mb-3">{course.highlight.title}</h2>
						<p class="text-ak-text-secondary max-w-3xl leading-relaxed mb-5">
							{course.highlight.description}
						</p>

						<button
							type="button"
							onclick={() => (enrollOpen = true)}
							class="inline-flex items-center justify-center gap-2 px-6 py-3 bg-ak-wintergreen text-ak-deep font-semibold rounded-md hover:opacity-90 active:opacity-80 transition-opacity touch-manipulation"
						>
							Enroll Now — Free
							<svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
								<path stroke-linecap="round" stroke-linejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
							</svg>
						</button>
					</div>
				</div>

				{#if course.nextStep}
					<div class="mt-4 p-4 sm:p-5 rounded-lg border border-ak-border-subtle bg-linear-card flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6 max-w-3xl">
						<div class="min-w-0 flex-1">
							<div class="text-xs font-semibold text-ak-wintergreen tracking-wider uppercase mb-1">After the session</div>
							<p class="text-sm text-ak-text-secondary">{course.nextStep.description}</p>
						</div>
						<a
							href={course.nextStep.href}
							class="shrink-0 inline-flex items-center justify-center gap-1.5 px-5 py-2.5 border border-ak-wintergreen/40 text-ak-wintergreen font-semibold rounded-md text-sm hover:bg-ak-wintergreen/10 transition-colors touch-manipulation"
						>
							{course.nextStep.label}
							<svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
								<path stroke-linecap="round" stroke-linejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
							</svg>
						</a>
					</div>
				{/if}
			</div>
		</section>
	{/if}
</main>

<!-- Mobile sticky CTA bar (hidden on lg+) -->
<div class="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-ak-deep/95 backdrop-blur-md border-t border-ak-border-subtle px-4 py-3 safe-area-bottom">
	<div class="flex items-center justify-between gap-3 max-w-lg mx-auto">
		<div>
			<div class="text-xs text-ak-text-muted">Free live session</div>
			<div class="font-display text-xl font-bold text-ak-wintergreen leading-tight">{course.cohort.sessionLength}</div>
		</div>
		<button
			type="button"
			onclick={() => (enrollOpen = true)}
			class="flex-shrink-0 px-6 min-h-11 py-2.5 bg-ak-wintergreen text-ak-deep font-semibold rounded-md text-sm hover:opacity-90 active:opacity-80 transition-opacity touch-manipulation"
		>
			Enroll Now
		</button>
	</div>
</div>

{#if enrollOpen}
	<EnrollDialog course={course} {icsHref} onClose={() => (enrollOpen = false)} />
{/if}
